import { useCallback, useRef, useState } from 'react';
import type React from 'react';

/**
 * Checkbox-row multi-selection with Shift-click RANGE support (BUG-2026-0188).
 *
 * The editable test-data tables (rock-tcs / rock-ucs ReadingsDataPanel) let the
 * user tick rows to bulk-delete, but before this hook every row had to be
 * ticked one at a time. This hook adds the universal "click one, Shift-click
 * another → everything in between" gesture on top of the existing single-toggle
 * and select-all behavior, and is shared so the two near-identical panels don't
 * each re-implement it.
 *
 * Rows are addressed by a stable string id (the panels' `_cid`). Callers pass
 * the FULL ordered id list (draft order, across pages) plus the clicked row's
 * absolute index, so a range can span table pages.
 */
export function useRowRangeSelection() {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Anchor = absolute index of the last row toggled without Shift; the far end
  // of a Shift-click range. Null until the first plain click.
  const anchorRef = useRef<number | null>(null);
  // Shift state captured on the checkbox's click (fires before its change), so
  // the change handler knows whether this was a range gesture.
  const shiftRef = useRef(false);

  const isSelected = useCallback((id: string) => selected.has(id), [selected]);

  /** Reset the whole selection (enter/cancel edit, after save, after bulk delete). */
  const clear = useCallback(() => {
    setSelected(new Set());
    anchorRef.current = null;
  }, []);

  /** Drop one id from the selection (single-row delete). */
  const removeId = useCallback((id: string) => {
    setSelected((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  /** Header checkbox: select all when not all selected, otherwise clear. */
  const toggleSelectAll = useCallback((allIds: string[]) => {
    setSelected((prev) => (allIds.length > 0 && prev.size === allIds.length ? new Set() : new Set(allIds)));
    anchorRef.current = null;
  }, []);

  const applyToggle = useCallback((orderedIds: string[], index: number, shiftKey: boolean) => {
    // Read the anchor NOW, outside the updater. React may defer a functional
    // setState updater to render time (whenever it can't compute it eagerly —
    // e.g. another update is already queued on this component, or inside
    // act()/batching), and by then anchorRef has been re-pointed at THIS row,
    // so `anchor !== index` would be false and the range would silently
    // collapse to a single toggle. Snapshotting first makes the gesture
    // independent of React's scheduling.
    const anchor = anchorRef.current;
    anchorRef.current = index;
    setSelected((prev) => {
      const next = new Set(prev);
      const clickedId = orderedIds[index];
      if (clickedId === undefined) return prev;
      if (shiftKey && anchor !== null && anchor !== index && anchor < orderedIds.length) {
        // Range: every row between anchor and the clicked row takes the clicked
        // row's NEW state (select if it was unselected, else deselect).
        const start = Math.min(anchor, index);
        const end = Math.max(anchor, index);
        const shouldSelect = !prev.has(clickedId);
        for (let i = start; i <= end; i += 1) {
          const id = orderedIds[i];
          if (id === undefined) continue;
          if (shouldSelect) next.add(id);
          else next.delete(id);
        }
      } else if (next.has(clickedId)) {
        next.delete(clickedId);
      } else {
        next.add(clickedId);
      }
      return next;
    });
  }, []);

  /**
   * Props to spread onto a row Checkbox. `onClick` captures the Shift modifier
   * (and clears any stray text selection the Shift-click would otherwise make),
   * `onChange` performs the toggle. A keyboard toggle (Space) never fires the
   * click, so shiftRef is reset each change and defaults to a plain toggle.
   */
  const getRowCheckboxProps = useCallback((orderedIds: string[], index: number) => ({
    checked: selected.has(orderedIds[index]),
    onClick: (e: React.MouseEvent) => {
      shiftRef.current = e.shiftKey;
      if (e.shiftKey) window.getSelection()?.removeAllRanges();
    },
    onChange: () => {
      applyToggle(orderedIds, index, shiftRef.current);
      shiftRef.current = false;
    },
  }), [selected, applyToggle]);

  return { selected, isSelected, clear, removeId, toggleSelectAll, getRowCheckboxProps };
}
